import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function StudioRegister() {
  const [inviteCode, setInviteCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUserDirectly } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!inviteCode.trim()) {
      setError('Invite code is required');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post<{ token: string; user: any; tenant: any }>('/auth/register-studio', {
        inviteCode: inviteCode.trim().toUpperCase(),
        email,
        password,
      });

      // Store token and redirect to studio dashboard
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Update auth context
      setUserDirectly(response.data.user);

      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page studio-auth">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-logo">MediaRite</h1>
          <p className="auth-subtitle">Studio Registration</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Register Your Studio</h2>
          <p className="form-note">Enter your invite code to create your studio account</p>

          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="inviteCode">Invite Code *</label>
            <input
              type="text"
              id="inviteCode"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              required
              placeholder="Enter your invite code"
              className="invite-code-input"
              style={{ textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'monospace' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Admin Email *</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="admin@yourstudio.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Create a password"
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Confirm your password"
              minLength={8}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating Studio...' : 'Create Studio Account'}
          </button>

          <p className="auth-footer">
            Already have an account? <Link to="/studiologin">Sign In</Link>
          </p>

          <p className="auth-note">
            Don't have an invite code? Contact your system administrator.
          </p>
        </form>
      </div>
    </div>
  );
}
